import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const SPEEDS = { 1: 10, 5: 50, 20: 200 };
const hm = (t) => { const [h, m] = String(t || '').split(':').map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null; };
export const clockOf = (min) => { const m = ((Math.round(min) % 1440) + 1440) % 1440; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };

function timedPath(geometry, t0, dur) {
  const d = [0];
  for (let i = 1; i < geometry.length; i += 1) d.push(d[i - 1] + Math.hypot(geometry[i][0] - geometry[i - 1][0], geometry[i][1] - geometry[i - 1][1]));
  const total = d[d.length - 1] || 1;
  return geometry.map((p, i) => ({ lat: p[0], lng: p[1], t: t0 + (dur * d[i]) / total }));
}

// Depo → Start → Stop → Depo zaman çizelgesi: her bacak kendi süresinde geometri boyunca ilerler.
export function buildSim(data) {
  const w = data?.work;
  const by = Object.fromEntries((data?.legs || []).map((l) => [l.key, l]));
  if (!w || w.tour_min == null || !by.start_stop) return null;
  const segs = [];
  let t = 0;
  [['depot_start', w.drive_out_min], ['start_stop', w.tour_min], ['stop_depot', w.drive_back_min]].forEach(([k, dur]) => {
    if (!by[k]) return;
    segs.push({ key: k, t0: t, dur: dur || 0, path: timedPath(by[k].geometry, t, dur || 0) });
    t += dur || 0;
  });
  return { segs, total: t, depart: hm(w.depart_at) ?? 0, path: segs.flatMap((s) => s.path) };
}

export function posAt(sim, t) {
  const seg = sim.segs.find((s) => t <= s.t0 + s.dur) || sim.segs[sim.segs.length - 1];
  const p = seg.path;
  let i = p.findIndex((x) => x.t >= t);
  if (i <= 0) i = i === 0 ? 0 : p.length - 1;
  const a = p[Math.max(0, i - 1)];
  const b = p[i];
  const f = b.t === a.t ? 1 : Math.min(1, Math.max(0, (t - a.t) / (b.t - a.t)));
  const done = t >= sim.total;
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f, phase: done ? 'end' : seg.key };
}

export function useRouteSim(data, defaultSpeed) {
  const sim = useMemo(() => buildSim(data), [data]);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(defaultSpeed || 1);
  useEffect(() => { if (defaultSpeed) setSpeed(defaultSpeed); }, [defaultSpeed]);
  const last = useRef(null);
  useEffect(() => { setT(0); setPlaying(false); }, [sim]);
  useEffect(() => {
    if (!playing || !sim) return undefined;
    let raf;
    const tick = (now) => {
      const dt = last.current == null ? 0 : (now - last.current) / 1000;
      last.current = now;
      setT((cur) => {
        const nx = cur + dt * SPEEDS[speed];
        if (nx >= sim.total) { setPlaying(false); return sim.total; }
        return nx;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); last.current = null; };
  }, [playing, speed, sim]);
  const toggle = useCallback(() => {
    if (!sim) return;
    if (!playing && t >= sim.total) setT(0);
    setPlaying((p) => !p);
  }, [sim, playing, t]);
  const pos = sim ? posAt(sim, t) : null;
  const trail = sim ? [...sim.path.filter((p) => p.t <= t).map((p) => [p.lat, p.lng]), [pos.lat, pos.lng]] : [];
  return { sim, t, setT, playing, toggle, speed, setSpeed, pos, trail, clock: sim ? clockOf(sim.depart + t) : null };
}
